import { Box, ChevronRight, MapPin } from "lucide-react";
import { Badge, Text } from "@musa.tech/design-system";
import { cn } from "@/lib/utils";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useDemoMode } from "@/contexts/DemoModeContext";
import { maskEstablishmentName, maskAddress } from "@/utils/demoMode";
import { removeGeneratorPrefix } from "@/utils/establishmentUtils";
import { getMaterialDisplayLabel } from "@/utils/materialSystem";
import {
  coletaStatusBadgeVariant,
  coletaStatusLabel,
  formatFullAddress,
} from "@/utils/coletasCalendar";
import type { CollectForTable } from "@/hooks/useCollectsApiData";

interface ColetaCalendarCardProps {
  collect: CollectForTable;
  /** "row" = linha larga (Agenda); "cell" = card compacto (Semana/Mês). */
  layout?: "row" | "cell";
  onSelect?: (collect: CollectForTable) => void;
  className?: string;
}

/** Card de uma coleta planejada: Ponto · Material · Cidade/UF. Clicável (abre detalhe). */
export const ColetaCalendarCard = ({
  collect,
  layout = "cell",
  onSelect,
  className,
}: ColetaCalendarCardProps) => {
  const { isDemoMode } = useDemoMode();
  const { trackButtonClick } = useAnalytics();

  /**
   * Nome único nas quatro visões que usam este card — Agenda, Semana, Mês e o
   * modal "Dia" (GR-712, Suposição 09). Só interessa que o detalhe foi aberto,
   * não de onde.
   */
  const handleSelect = () => {
    trackButtonClick({
      buttonLabel: "proximas_coletas_visualizar_detalhe",
      collection_id: collect.collect_id,
    });
    onSelect?.(collect);
  };

  const rawName =
    collect.establishments?.establishment_name ||
    collect.establishment_name ||
    "";
  const establishmentName = maskEstablishmentName(
    removeGeneratorPrefix(rawName, isDemoMode),
    isDemoMode,
  );
  const material = getMaterialDisplayLabel(collect.kind_of_material || "");
  const packaging = collect.packaging_name ?? null;
  const rawAddressLines = formatFullAddress({
    street: collect.street,
    number: collect.number,
    neighborhood: collect.neighborhood,
    city: collect.city,
    state: collect.state,
  });
  const addressLines = isDemoMode && rawAddressLines.length > 0
    ? [maskAddress(rawAddressLines[0], isDemoMode)]
    : rawAddressLines;
  const statusText = coletaStatusLabel(collect.sla_status);

  const materialBadge = material ? (
    <Badge
      variant="info"
      iconLeft={<Box className="h-3 w-3 shrink-0" aria-hidden />}
      className="max-w-[160px] shrink-0 overflow-hidden [&>p]:min-w-0 [&>p]:truncate"
      title={material}
    >
      {material}
    </Badge>
  ) : null;

  const packagingBadge = packaging ? (
    <Badge
      variant="secondary"
      className="max-w-[160px] shrink-0 overflow-hidden [&>p]:min-w-0 [&>p]:truncate"
      title={packaging}
    >
      {packaging}
    </Badge>
  ) : null;

  if (layout === "row") {
    return (
      <button
        type="button"
        data-testid="proximas-coletas-detalhe"
        onClick={handleSelect}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-neutral-50",
          className,
        )}
      >
        <MapPin className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
        <div className="min-w-0 flex-1">
          <Text variant="label" className="block truncate text-neutral-900">
            {establishmentName || "—"}
          </Text>
          {addressLines.map((line, i) => (
            <Text
              key={i}
              variant="body"
              size="small"
              className="block truncate text-neutral-500"
            >
              {line}
            </Text>
          ))}
        </div>
        {materialBadge}
        {packagingBadge}
        <Badge
          variant={coletaStatusBadgeVariant(collect.sla_status)}
          className="shrink-0 whitespace-nowrap"
        >
          {statusText}
        </Badge>
        <ChevronRight className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
      </button>
    );
  }

  return (
    <button
      type="button"
      data-testid="proximas-coletas-detalhe"
      onClick={handleSelect}
      className={cn(
        "block w-full rounded-md border border-neutral-200 border-l-[3px] border-l-primary-400 bg-white px-2 py-1.5 text-left shadow-sm transition-all hover:border-primary-300 hover:border-l-primary-600 hover:bg-primary-50 hover:shadow",
        className,
      )}
    >
      <Text
        variant="label"
        size="small"
        className="block truncate font-semibold text-neutral-900"
      >
        {establishmentName || "—"}
      </Text>
      {material && (
        <Text variant="body" size="small" className="block truncate text-neutral-600">
          {material}
        </Text>
      )}
      {packaging && (
        <Text variant="body" size="small" className="block truncate text-neutral-500">
          {packaging}
        </Text>
      )}
      {addressLines.length > 0 && (
        <Text variant="body" size="small" className="block truncate text-neutral-400">
          {addressLines[addressLines.length - 1]}
        </Text>
      )}
    </button>
  );
};
